// The legacy escape hatch (docs/18-one-fee-reserve.md 2.4, "A legacy device on a browser that fails the probe" and
// "The escape hatch's lifetime", R85). On a device that stores an older per-device fee reserve envelope
// (`sealedRelayer`) and a browser whose worker cannot hold the fee key, unlock refuses; this operation sends that
// older reserve's SOL to an address the user types, on today's main-thread primitives: the device passphrase opens the
// envelope (`openRelayerSeed`), `relayerFromSeed` imports it non-extractable, and one System transfer signed by that
// key alone goes out (as Send out builds it, engine.ts `sendGas`). It unlocks nothing else (no master, no vault
// action, no derived key, no worker), opens no ledger, and writes nothing to storage, not even a pre-R48 address.
//
// One operation per tab, owned by the session (`Session.legacyOp`, `beginLegacySendOut`). It holds everything secret
// or signable in the flow: the envelope copy, the opened seed while it exists, the imported signer until its one
// signature, the signed wire until its send, its own two Rpc clients and an AbortController. The form
// (components/LegacySendOutForm.tsx) holds only `handle`: methods and public figures, never a seed, signer or wire.
// Neither client keeps an evidence log (`QuietRpc`), since the send's params carry the signed wire, and the handle
// (built outside the class, `handleOver`) reaches the operation through a reference the operation lets go of once it
// has ended (step 6), so a form that keeps its handle keeps nothing of the operation alive; `figures()` then returns
// the last snapshot.
//
// `live()` (step 4): not cancelled, the session's token unchanged, no forget pending. It is checked after every await,
// immediately before signing and before every send attempt. The first Rpc client (reads, simulation, send) refuses
// any request while `live()` fails and its backoff sleep rejects, so `Rpc.call` makes no further attempt; the second
// (the confirmation's polls) stops only when the controller is aborted. `cancel(reason)` is synchronous and
// idempotent: it aborts the controller, except after the one-send latch for a lock, an auto-lock, a dismissal or an
// unlock, so the confirmation of a transaction already issued may finish (steps 3 and 5). Cleanup runs on every path
// (step 6): seeds zeroed, references dropped, the controller aborted, and the slot released once the last await of
// the flow has settled.

import { address } from '@solana/kit';
import { MasterStorageError, type SealedEnvelope, type SendOutRefusal, addressBytes, equalBytes, feeOf, openRelayerSeed, sendOutCheck, sendOutLimits } from '@thevault/sdk';
import { type Instruction, PLAIN_LIMITS, type V1Message, compileV1 } from '@thevault/sdk/v1';
import { WrongNetwork, assertDeployment } from './deployment';
import { EvidenceLog, RelayerError, type RelayerSigner, Rpc, fromBase58, relayerFromSeed, signV1, toBase58 } from './rpc';
import { type LegacyIdentity, loadLegacyIdentity } from './storage';

/**
 * Where the operation stands. User waits: 'opening' while a pre-R48 device waits for the passphrase that learns the
 * older address (and while that opening runs), 'review' while the destination and amount are entered and reviewed.
 * Ended: 'done', 'cancelled', 'failed'.
 */
export type LegacyPhase = 'entry' | 'opening' | 'reading' | 'review' | 'passphrase' | 'importing' | 'recheck' | 'blockhash' | 'identity' | 'signing' | 'simulating' | 'sending' | 'confirming' | 'done' | 'cancelled' | 'failed';

/** What ended the operation (step 3). 'setup-changed' is the identity re-read's own cancel. */
export type LegacyCancelReason = 'lock' | 'auto-lock' | 'dismissed' | 'unlock' | 'forget' | 'versionchange' | 'pagehide' | 'setup-changed';

/** Step 3: these abort the controller in every phase, the confirmation of a transaction already issued included; the others leave it alone after the latch. */
const ABORTS_ALWAYS: ReadonlySet<LegacyCancelReason> = new Set<LegacyCancelReason>(['forget', 'versionchange', 'pagehide', 'setup-changed']);

const STOPPED: Record<LegacyCancelReason, string> = {
  lock: 'The app locked, so the send-out stopped.',
  'auto-lock': 'The app locked after a while without activity, so the send-out stopped.',
  dismissed: 'The send-out was cancelled.',
  unlock: 'An unlock started, so the send-out stopped.',
  forget: 'This device is being forgotten, so the send-out stopped.',
  versionchange: "Another tab removed or replaced this device's data, so the send-out stopped.",
  pagehide: 'The page was left, so the send-out stopped.',
  'setup-changed': "This device's setup changed since the send-out began, so it stopped.",
};

/**
 * How the operation ended, for the form. `landed`: confirmed. `not-sent`: nothing was sent. `changed`: the recheck
 * after the passphrase found figures that no longer give the reviewed amount and fee; nothing was signed, and the
 * review is shown again by a new operation. `failed`: it landed and failed on chain, so the amount did not move and
 * the older reserve paid the network fee. `submitted`: the transfer was handed to the network but its landing was not
 * confirmed (the wait timed out, or a Forget, version change or tab close stopped the wait).
 */
export type LegacyOutcome = { kind: 'landed'; signature: string } | { kind: 'not-sent'; reason: string } | { kind: 'changed'; reason: string } | { kind: 'failed'; signature: string; reason: string } | { kind: 'submitted'; signature: string; reason: string };

/** The public figures the form shows; nothing here is secret. Balances in lamports. */
export interface LegacyFigures {
  /** The older reserve's address; null on a pre-R48 device until its first opening. */
  from: string | null;
  to: string | null;
  /** L, the older reserve's balance, from the latest read. */
  older: bigint | null;
  /** The destination's balance, read at review. */
  destination: bigint | null;
  /** F, the account minimum. */
  minimum: bigint | null;
  /** The reviewed amount; null until `prepare` succeeds. */
  amount: bigint | null;
  fee: bigint;
  phase: LegacyPhase;
  error: string | null;
  /** The reviewed amount is "everything but the fee". */
  everything: boolean;
  /** A pre-R48 device: the passphrase must open the envelope once to learn the older address before the reads. */
  needsAddress: boolean;
  /** The one-send latch is set: a transfer has been handed to the network. */
  submitted: boolean;
  /** Why the operation was cancelled, also after the latch (where the confirmation may still finish). */
  stoppedBy: LegacyCancelReason | null;
  signature: string | null;
  outcome: LegacyOutcome | null;
}

/** What the form holds (step 1): methods and figures only. */
export interface LegacySendOutHandle {
  /** Entry (step 2), then the network check, the older balance and F; a pre-R48 device stops at 'opening' unless `carry` names the address an earlier operation opened. */
  open(carry?: { from: string }): Promise<void>;
  /** Pre-R48 only: open the envelope once to learn the older address (the signer is dropped at once), then read. */
  openAddress(passphrase: string): Promise<void>;
  figures(): LegacyFigures;
  /** Read the destination and the balances again and apply Send out's rule; the review phase follows. An input the rule refuses rejects with `LegacyInputRefused` and leaves the operation in review. */
  prepare(destination: string, amount: bigint | 'everything'): Promise<void>;
  /** The passphrase after Confirm: accepted once, in review. Resolves the signature when the transfer landed, null otherwise (the outcome is in `figures()`). */
  confirm(passphrase: string): Promise<{ signature: string } | null>;
  cancel(reason: LegacyCancelReason): void;
  subscribe(listener: () => void): () => void;
}

/** What the operation needs from the session that owns it. */
export interface LegacySession {
  /** The session's token (`generation`); a lock or forget increments it. */
  generation(): number;
  /** A forget is pending or blocked. */
  forgetting(): boolean;
  /** Every lock run and runtime teardown in flight has ended. */
  settled(): Promise<void>;
  /** The RPC endpoint the clients use, read at entry. */
  rpcUrl: string;
  /** Called once, when the operation has ended and its last await has settled: the session frees the slot. */
  released(op: LegacySendOut): void;
  /** Called synchronously when the operation is cancelled: the session stops the timers it armed for it (step 3, auto-lock), which run only while the operation is live. */
  stopped(op: LegacySendOut): void;
}

/** The operation stopped: cancelled, or its lifetime check failed. */
export class LegacyCancelled extends Error {
  constructor(readonly reason: LegacyCancelReason) {
    super(STOPPED[reason]);
    this.name = 'LegacyCancelled';
  }
}

/** A refusal that ends the operation before anything is sent (step 5: trying again is a new operation). */
export class LegacyRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LegacyRefused';
  }
}

/** An address or amount the review cannot take: the operation stays in review and nothing ends. */
export class LegacyInputRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LegacyInputRefused';
  }
}

/** The passphrase argument, in a holder the opening empties as soon as it has handed the text to the KDF (step 6: dropped, since a string cannot be zeroed). */
type Secret = { value: string | null };

/** One plain, single-signature action's fee: what Send out pays (5,000 + 10,000 priority, `PLAIN_LIMITS`). */
const FEE = feeOf(PLAIN_LIMITS);
const SYSTEM_PROGRAM_ID = new Uint8Array(32);

/** System program Transfer: u32le(2) || u64le(lamports), the older key the only signer (as engine.ts builds Send out). */
function systemTransfer(from: Uint8Array, to: Uint8Array, lamports: bigint): Instruction {
  const data = new Uint8Array(12);
  const view = new DataView(data.buffer);
  view.setUint32(0, 2, true);
  view.setBigUint64(4, lamports, true);
  return { programId: SYSTEM_PROGRAM_ID, accounts: [{ pubkey: from, isSigner: true, isWritable: true }, { pubkey: to, isSigner: false, isWritable: true }], data };
}

/** Lamports as SOL, for a sentence (up to nine decimals, trailing zeros dropped). */
export function solText(lamports: bigint): string {
  const sign = lamports < 0n ? '-' : '';
  const v = lamports < 0n ? -lamports : lamports;
  const frac = (v % 1_000_000_000n).toString().padStart(9, '0').replace(/0+$/, '');
  return `${sign}${v / 1_000_000_000n}${frac ? `.${frac}` : ''} SOL`;
}

/** Why Send out's rule (5.1 A) refuses an amount from the older reserve, in the user's words. */
function refusalText(reason: SendOutRefusal, r: { older: bigint; minimum: bigint; maxPartial: bigint | null; destination: bigint }): string {
  switch (reason) {
    case 'nothing':
      return 'Enter an amount to send.';
    case 'below-minimum':
      return `The older fee reserve holds ${solText(r.older)}, less than its account minimum of ${solText(r.minimum)} plus the ${solText(FEE)} fee, so nothing can be sent out of it.`;
    case 'over-balance':
      return `That is more than the older fee reserve holds after the ${solText(FEE)} fee.`;
    case 'leaves-below-minimum':
      return `That would leave the older fee reserve below its account minimum of ${solText(r.minimum)}. Send at most ${solText(r.maxPartial ?? 0n)}, or everything but the fee.`;
    case 'destination-below-minimum':
      return `The destination holds ${solText(r.destination)}, less than an account's minimum of ${solText(r.minimum)}, so it must end with at least that much: send at least ${solText(r.minimum - r.destination)}.`;
  }
}

/** The amount Send out's rule allows, or the sentence that refuses it: "everything but the fee" only when L ≥ F + fee, and a destination below F must end at F or more. */
function amountUnderRule(i: { older: bigint; minimum: bigint; destination: bigint; amount: bigint | 'everything' }): { ok: true; amount: bigint } | { ok: false; reason: string } {
  const limits = sendOutLimits(i.older, i.minimum, FEE);
  const text = (reason: SendOutRefusal) => refusalText(reason, { older: i.older, minimum: i.minimum, maxPartial: limits.maxPartial, destination: i.destination });
  if (i.amount === 'everything' && limits.everything === null) return { ok: false, reason: text('below-minimum') };
  const amount = i.amount === 'everything' ? limits.everything! : i.amount;
  const check = sendOutCheck({ balance: i.older, minimum: i.minimum, amount, fee: FEE, destinationLamports: i.destination });
  return check.ok ? { ok: true, amount } : { ok: false, reason: text(check.reason) };
}

function sameEnvelope(a: SealedEnvelope, b: SealedEnvelope): boolean {
  return a.v === b.v && a.kdf === b.kdf && a.iterations === b.iterations && a.salt === b.salt && a.iv === b.iv && a.ciphertext === b.ciphertext;
}

/** Step 3, setup replacement: the exact envelopes and the three `vaultInfo` fields, all equal. */
function sameIdentity(a: LegacyIdentity, b: LegacyIdentity): boolean {
  return sameEnvelope(a.sealedRelayer, b.sealedRelayer) && sameEnvelope(a.sealedMaster, b.sealedMaster) && a.vaultInfo.vaultId === b.vaultInfo.vaultId && a.vaultInfo.address === b.vaultInfo.address && a.vaultInfo.relayer === b.vaultInfo.relayer;
}

/**
 * 2.5's refusal as the door says it: `WrongNetwork`'s own sentence points to Settings, which opens only after unlock,
 * and this browser cannot unlock (2.4, "A legacy device on a browser that fails the probe").
 */
const DOOR_WRONG_NETWORK = 'This network is not the one this app was built for, or it could not be read. Settings opens only after unlock, which this browser cannot do: try again later, or restore from the 24 words on a supported browser. Nothing was sent.';

/** Step 5, already issued: the send returned no signature (every attempt failed at the network, or a cancel stopped a retry), so the transfer may never have arrived. */
const HANDED_NOT_CONFIRMED = "The transfer was handed to the network but not confirmed; it may not have arrived. The older reserve's balance shows whether it landed.";

/** A failure in the user's words; every path before the latch says nothing was sent. */
function failureText(e: unknown): string {
  if (e instanceof LegacyCancelled || e instanceof LegacyRefused || e instanceof LegacyInputRefused) return e.message;
  if (e instanceof WrongNetwork) return DOOR_WRONG_NETWORK;
  if (e instanceof RelayerError && e.code === 'RpcError') return `The network could not be read: ${e.message}. Nothing was sent.`;
  const raw = (e instanceof Error ? e.message : String(e)).trim();
  return `The send-out stopped: ${raw || 'an unknown error'}${/[.!?]$/.test(raw) ? '' : '.'} Nothing was sent.`;
}

const ENDED: ReadonlySet<LegacyPhase> = new Set<LegacyPhase>(['done', 'cancelled', 'failed']);

/** An evidence log that keeps nothing: the operation's clients must not retain what they sent (step 6), and the send's params carry the signed wire. */
class NoEvidence extends EvidenceLog {
  override record(): void {
    /* nothing is kept */
  }
}

/** `Rpc` with an evidence log that keeps nothing: the operation's two clients. */
class QuietRpc extends Rpc {
  override readonly evidence: EvidenceLog = new NoEvidence();
}

/** The operation's side of the handle: its own steps, reached only through `HandleRef.op`. */
interface HandlePorts {
  open(carry?: { from: string }): Promise<void>;
  openAddress(passphrase: Secret): Promise<void>;
  figures(): LegacyFigures;
  prepare(destination: string, amount: bigint | 'everything'): Promise<void>;
  confirm(passphrase: Secret): Promise<{ signature: string } | null>;
  cancel(reason: LegacyCancelReason): void;
  readonly listeners: Set<() => void>;
}

/** What the handle reaches the operation through: `op` is let go once the operation has ended (step 6), leaving `last`, its final figures. */
interface HandleRef {
  op: HandlePorts | null;
  last: LegacyFigures;
}

/**
 * The handle (step 1), built here rather than in the operation's constructor: closures made in one scope share it, and
 * the constructor's scope holds `this` (its fetch and sleep wrappers use it), so a handle made there would keep a
 * released operation alive for as long as a mounted form holds the handle (step 6). These closures capture only `ref`
 * (and `over`); once the operation has ended and `ref.op` is null, an ended handle refuses every step.
 */
function handleOver(ref: HandleRef): LegacySendOutHandle {
  const over = (): LegacyRefused => new LegacyRefused('This send-out has ended.');
  return {
    open: (carry) => (ref.op ? ref.op.open(carry) : Promise.reject(over())),
    // The passphrase travels in a holder the opening clears once it has taken it (step 6; a string cannot be zeroed).
    openAddress: (passphrase) => (ref.op ? ref.op.openAddress({ value: passphrase }) : Promise.reject(over())),
    figures: () => (ref.op ? ref.op.figures() : ref.last),
    prepare: (destination, amount) => (ref.op ? ref.op.prepare(destination, amount) : Promise.reject(over())),
    confirm: (passphrase) => (ref.op ? ref.op.confirm({ value: passphrase }) : Promise.resolve(null)),
    cancel: (reason) => ref.op?.cancel(reason),
    subscribe: (listener) => {
      ref.op?.listeners.add(listener);
      return () => {
        ref.op?.listeners.delete(listener);
      };
    },
  };
}

export class LegacySendOut {
  readonly handle: LegacySendOutHandle;
  private readonly session: LegacySession;
  private readonly controller = new AbortController();
  /** Reads, the simulation and the send: refuses while `live()` fails. */
  private readonly rpc: Rpc;
  /** The confirmation's `getSignatureStatuses` polls only: stops when the controller is aborted. */
  private readonly confirmRpc: Rpc;
  private readonly ref: HandleRef;
  private readonly listeners = new Set<() => void>();
  /** Pending backoff sleeps, woken by `cancel` so a stopped one rejects at once. */
  private readonly wakers = new Set<() => void>();
  // Secret or signable (step 1); dropped by `cancel` at once and by the cleanup on every path (step 6).
  #identity: LegacyIdentity | null = null;
  #seed: Uint8Array | null = null;
  #signer: RelayerSigner | null = null;
  #wire: Uint8Array | null = null;
  /** The session token captured at entry (step 2); null before. */
  private gen: number | null = null;
  private stoppedBy: LegacyCancelReason | null = null;
  /** The reason that aborted the controller, when one did. */
  private abortedBy: LegacyCancelReason | null = null;
  private latched = false;
  /** The first client's requests made after the latch, all of them `sendTransaction` attempts: a refusal is the network's answer only when it answered the first (step 5). */
  private sendAttempts = 0;
  /** The send returned the signature: the network accepted the transfer. */
  private sendReturned = false;
  /** A refusal that answered a retry, after an attempt that may have reached the network: not taken as the answer. */
  private retryRefusal: string | null = null;
  /** DEV read-out only: how many times a signer was assigned to the operation. */
  private signerInstalls = 0;
  private opened = false;
  private busy = 0;
  private releasedFlag = false;
  /** The older reserve's public key: from `vaultInfo.relayer`, or the first opening's key on a pre-R48 device. Every opening must give this key. */
  private sourceKey: Uint8Array | null = null;
  private destinationKey: Uint8Array | null = null;
  private reviewed: { amount: bigint | 'everything' } | null = null;
  private snap: LegacyFigures = { from: null, to: null, older: null, destination: null, minimum: null, amount: null, fee: FEE, phase: 'entry', error: null, everything: false, needsAddress: false, submitted: false, stoppedBy: null, signature: null, outcome: null };

  constructor(session: LegacySession) {
    this.session = session;
    const signal = this.controller.signal;
    // Resolved per request, as `Rpc`'s default does, so a page-level wrapper of `fetch` sees these requests too.
    const fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init);
    // Step 4: every request carries the controller's signal; the first client refuses, without fetching, any request
    // made while `live()` fails (the first send attempt and every retry included), and its backoff rejects once
    // `live()` fails, which ends `Rpc.call` instead of another attempt.
    this.rpc = new QuietRpc({
      url: session.rpcUrl,
      fetchImpl: (input, init) => {
        if (!this.live()) return Promise.reject(this.stopped());
        // After the latch only `sendTransaction` runs on this client, so this counts its attempts (step 5).
        if (this.latched) this.sendAttempts++;
        return fetchImpl(input, { ...init, signal });
      },
      sleepImpl: (ms) => this.sleep(ms, () => !this.live()),
    });
    // The confirmation's polls of a transaction already issued outlive a lock or a dismissal and end on an abort.
    this.confirmRpc = new QuietRpc({
      url: session.rpcUrl,
      fetchImpl: (input, init) => (signal.aborted ? Promise.reject(this.stopped()) : fetchImpl(input, { ...init, signal })),
      sleepImpl: (ms) => this.sleep(ms, () => signal.aborted),
    });
    // The handle reaches these ports only through `ref`, which `settle` empties, so once the operation has ended a
    // handle the form still holds keeps nothing of it alive (step 6; `handleOver`).
    this.ref = {
      op: {
        open: (carry) => this.open(carry),
        openAddress: (passphrase) => this.openAddress(passphrase),
        figures: () => this.snap,
        prepare: (destination, amount) => this.prepare(destination, amount),
        confirm: (passphrase) => this.confirm(passphrase),
        cancel: (reason) => this.cancel(reason),
        listeners: this.listeners,
      },
      last: this.snap,
    };
    this.handle = handleOver(this.ref);
  }

  /** The DEV-only read-out (docs/18 section 10): the phase, whether anything secret is held and how many times a signer was installed, never their contents. */
  readout(): { phase: LegacyPhase; holdsSigner: boolean; holdsWire: boolean; holdsEnvelope: boolean; holdsSeed: boolean; signerInstalls: number } {
    return { phase: this.snap.phase, holdsSigner: this.#signer !== null, holdsWire: this.#wire !== null, holdsEnvelope: this.#identity !== null, holdsSeed: this.#seed !== null, signerInstalls: this.signerInstalls };
  }

  /** Step 4: not cancelled, the session's token unchanged since entry, and no forget pending. */
  live(): boolean {
    return this.stoppedBy === null && (this.gen === null || this.gen === this.session.generation()) && !this.session.forgetting();
  }

  /**
   * Step 3. Synchronous and idempotent: from this instant `live()` fails, and everything secret or signable is dropped
   * (the seed zeroed). The controller is aborted, rejecting every pending request and backoff of both clients, except
   * after the latch for a lock, an auto-lock, a dismissal or an unlock: the confirmation of the transaction already
   * issued may then finish. A later call with an aborting reason still aborts. The slot is released once the flow's
   * last await has settled (step 6), at once when none is pending.
   */
  cancel(reason: LegacyCancelReason): void {
    if (this.stoppedBy === null && !ENDED.has(this.snap.phase)) {
      this.stoppedBy = reason;
      this.dropSecrets();
      if (this.latched) this.patch({ stoppedBy: reason });
      else this.patch({ phase: 'cancelled', stoppedBy: reason, error: `${STOPPED[reason]} Nothing was sent.`, outcome: { kind: 'not-sent', reason: `${STOPPED[reason]} Nothing was sent.` } });
      // No longer live: the idle and hidden-tab timers armed for it stop now, not at its release, so one cannot fire
      // during the confirmation that may still run and lock over a later unlock.
      this.session.stopped(this);
    }
    if ((!this.latched || ABORTS_ALWAYS.has(reason)) && !this.controller.signal.aborted) {
      this.abortedBy = reason;
      this.controller.abort();
    }
    for (const wake of [...this.wakers]) wake();
    this.settle();
  }

  // ------------------------------------------------------------------ the flow

  /** Step 2 and the reads before review. */
  private async open(carry?: { from: string }): Promise<void> {
    if (this.opened || this.snap.phase !== 'entry') throw new LegacyRefused('This send-out has already started.');
    this.opened = true;
    await this.flow(async () => {
      // As unlock does: a lock run or teardown in flight finishes first; then the forget state and the token.
      await this.session.settled();
      this.check();
      this.gen = this.session.generation();
      const identity = await loadLegacyIdentity();
      this.check();
      if (!identity) throw new LegacyRefused('This device holds no older fee reserve to send out. Nothing was sent.');
      this.#identity = identity;
      const recorded = identity.vaultInfo.relayer;
      if (recorded !== undefined || carry) {
        // The stored address, or the one an earlier operation of this tab opened (pre-R48): either way the opening at
        // Confirm must give this key, or the operation ends before signing.
        const from = recorded ?? carry!.from;
        this.sourceKey = addressBytes(address(from));
        this.patch({ from });
        await this.readOlder();
        this.check();
        this.patch({ phase: 'review' });
      } else {
        // Pre-R48 (`vaultInfo.relayer` absent): the address is unknown until the envelope is opened once.
        this.patch({ phase: 'opening', needsAddress: true });
      }
    });
  }

  /** The pre-R48 address opening, before the balance reads: the signer is dropped at once and only its public key kept (step 4's table). */
  private async openAddress(passphrase: Secret): Promise<void> {
    if (this.snap.phase !== 'opening' || !this.snap.needsAddress || this.busy > 0) throw new LegacyRefused('The older reserve is not waiting to be opened.');
    await this.flow(async () => {
      const key = await this.publicKeyOf(passphrase);
      // The caller resumes a microtask after the callee's own check, so it checks again.
      this.check();
      this.sourceKey = key;
      this.patch({ from: toBase58(key), needsAddress: false });
      await this.readOlder();
      this.check();
      this.patch({ phase: 'review' });
    });
  }

  /** The network check (2.5), then L and F. */
  private async readOlder(): Promise<void> {
    this.patch({ phase: 'reading' });
    await assertDeployment(this.rpc);
    this.check();
    const [older, minimum] = await Promise.all([this.rpc.getBalance(this.snap.from!), this.rpc.getMinimumBalanceForRentExemption(0)]);
    this.check();
    this.patch({ older, minimum });
  }

  private async prepare(destination: string, amount: bigint | 'everything'): Promise<void> {
    if (this.snap.phase !== 'review' || this.busy > 0) throw new LegacyRefused('The send-out is not waiting for a destination.');
    // Leaving the review: the previous amount is no longer reviewed, so Confirm waits for this one.
    this.reviewed = null;
    this.destinationKey = null;
    this.patch({ amount: null, to: null, destination: null, everything: false, error: null });
    let to: Uint8Array;
    try {
      to = addressBytes(address(destination.trim()));
    } catch {
      this.patch({ error: 'That is not a Solana address.' });
      throw new LegacyInputRefused('That is not a Solana address.');
    }
    if (equalBytes(to, this.sourceKey!)) {
      this.patch({ error: 'That is the older fee reserve itself. Enter another address.' });
      throw new LegacyInputRefused('That is the older fee reserve itself. Enter another address.');
    }
    await this.flow(async () => {
      this.patch({ phase: 'reading' });
      const target = toBase58(to);
      const [older, minimum, account] = await Promise.all([this.rpc.getBalance(this.snap.from!), this.rpc.getMinimumBalanceForRentExemption(0), this.rpc.getAccountInfo(target)]);
      this.check();
      const held = account?.lamports ?? 0n;
      this.patch({ older, minimum, destination: held, to: target });
      const rule = amountUnderRule({ older, minimum, destination: held, amount });
      if (!rule.ok) throw new LegacyInputRefused(rule.reason);
      this.destinationKey = to;
      this.reviewed = { amount };
      this.patch({ phase: 'review', amount: rule.amount, everything: amount === 'everything' });
    });
  }

  /**
   * Step 5. Accepted once, in review, and it leaves review synchronously, so a second click does nothing. The
   * passphrase opening, the import, the recheck, the blockhash, the identity re-read, the signature, the simulation,
   * the latch, the send and the confirmation, with `live()` after each await.
   */
  private async confirm(passphrase: Secret): Promise<{ signature: string } | null> {
    if (this.snap.phase !== 'review' || this.busy > 0 || !this.reviewed || !this.destinationKey || this.snap.amount === null) return null;
    const reviewedAmount = this.snap.amount;
    const reviewed = this.reviewed;
    const to = this.destinationKey;
    this.patch({ phase: 'passphrase', error: null });
    try {
      return await this.flow(() => this.send(passphrase, reviewed.amount, reviewedAmount, to));
    } catch {
      // The outcome is in the figures; a refusal never escapes as an unhandled rejection of the form's press.
      return null;
    }
  }

  private async send(passphrase: Secret, input: bigint | 'everything', reviewedAmount: bigint, to: Uint8Array): Promise<{ signature: string } | null> {
    await this.installSigner(passphrase);
    this.check();
    // The recheck: the network check, both balances and F must give the reviewed amount and fee under the same rule.
    this.patch({ phase: 'recheck' });
    await assertDeployment(this.rpc);
    this.check();
    const [older, minimum, account] = await Promise.all([this.rpc.getBalance(this.snap.from!), this.rpc.getMinimumBalanceForRentExemption(0), this.rpc.getAccountInfo(toBase58(to))]);
    this.check();
    const held = account?.lamports ?? 0n;
    const rule = amountUnderRule({ older, minimum, destination: held, amount: input });
    if (!rule.ok || rule.amount !== reviewedAmount) {
      // A changed figure ends this operation before anything is signed; the form shows the review again in a new one,
      // whose Confirm asks for the passphrase again.
      this.#signer = null;
      const reason = rule.ok ? `The older fee reserve now holds ${solText(older)}, so the amount is ${solText(rule.amount)}, not ${solText(reviewedAmount)}. Review it again. Nothing was sent.` : `${rule.reason} Review it again. Nothing was sent.`;
      this.patch({ phase: 'failed', older, minimum, destination: held, error: reason, outcome: { kind: 'changed', reason } });
      return null;
    }
    this.patch({ phase: 'blockhash' });
    const bh = await this.rpc.getLatestBlockhash();
    this.check();
    // Step 3, setup replacement: the identity read again, read-only, as the last await before signing. A difference,
    // or a failed read, cancels.
    this.patch({ phase: 'identity' });
    let again: LegacyIdentity | undefined;
    try {
      again = await loadLegacyIdentity();
    } catch {
      again = undefined;
    }
    this.check();
    if (!again || !this.#identity || !sameIdentity(again, this.#identity)) {
      this.cancel('setup-changed');
      throw new LegacyCancelled('setup-changed');
    }
    this.patch({ phase: 'signing' });
    // Immediately before signing: synchronous from this check to the sign call.
    this.check();
    const from = this.sourceKey!;
    const wire = await this.signOnce(compileV1(from, [systemTransfer(from, to, reviewedAmount)], bh.blockhash, PLAIN_LIMITS));
    // A late signature after a cancel is dropped here with its wire; nothing is sent.
    this.check();
    this.#wire = wire;
    this.patch({ phase: 'simulating' });
    const sim = await this.rpc.simulateTransaction(wire);
    this.check();
    if (sim.err) throw new LegacyRefused(`The network's pre-flight check refused the transfer (${JSON.stringify(sim.err)}). Nothing was sent.`);
    // The signature the confirmation polls, taken from the wire itself (its last 64 bytes), so it needs no reply.
    const signature = toBase58(wire.subarray(wire.length - 64));
    this.patch({ phase: 'sending', signature });
    // Immediately before the send: synchronous from this check through the one-way latch to the first attempt, whose
    // request `sendTransaction` starts before its first await. The figures say so only once it has started.
    this.check();
    this.latched = true;
    // `beforeAttempt` runs before every attempt, retries included: a failed `live()` permits none.
    const sent = this.rpc.sendTransaction(wire, { beforeAttempt: () => this.check() });
    this.patch({ submitted: true });
    let refusal: string | null = null;
    try {
      await sent;
      this.sendReturned = true;
    } catch (e) {
      // The node's own JSON-RPC refusal is an answer only when it answered the first attempt: nothing was accepted.
      // A retry follows an attempt that may have reached the network, whose landing a retry's refusal (a lapsed
      // blockhash, one already processed) says nothing about; it, a network error and a cancel that stopped a retry
      // all leave the outcome to the confirmation.
      if (e instanceof RelayerError && e.code === 'RpcError' && e.detail !== undefined) {
        if (this.sendAttempts <= 1) refusal = e.message;
        else this.retryRefusal = e.message;
      }
    } finally {
      this.#wire = null;
    }
    if (refusal !== null) {
      const reason = `The network refused the transfer when it was submitted (${refusal}). Nothing moved.`;
      this.patch({ phase: 'failed', error: reason, outcome: { kind: 'not-sent', reason } });
      return null;
    }
    return this.confirmation(signature, reviewedAmount);
  }

  /** Step 5, already issued: polls the signature taken from the wire itself, through the second client. */
  private async confirmation(signature: string, amount: bigint): Promise<{ signature: string } | null> {
    if (this.controller.signal.aborted) return this.unconfirmed(signature, this.abortStopText());
    this.patch({ phase: 'confirming' });
    try {
      await this.confirmRpc.confirm(signature);
    } catch (e) {
      if (this.controller.signal.aborted) return this.unconfirmed(signature, this.abortStopText());
      if (e instanceof RelayerError && e.code === 'ConfirmFailed') {
        // A landed transaction pays its fee whatever its instruction did: the older reserve paid it, and only the amount stayed.
        const reason = `The transfer landed and failed on chain (${JSON.stringify(e.detail)}): the ${solText(amount)} did not move, and the ${solText(FEE)} network fee was paid from the older fee reserve.`;
        this.patch({ phase: 'failed', error: reason, outcome: { kind: 'failed', signature, reason } });
        return null;
      }
      return this.unconfirmed(signature, this.unconfirmedText());
    }
    this.patch({ phase: 'done', error: null, outcome: { kind: 'landed', signature } });
    return { signature };
  }

  private unconfirmed(signature: string, reason: string): null {
    this.patch({ phase: this.controller.signal.aborted ? 'cancelled' : 'failed', error: reason, outcome: { kind: 'submitted', signature, reason } });
    return null;
  }

  /** Not confirmed: only a send that returned the signature says the transfer was sent; otherwise it may never have arrived. */
  private unconfirmedText(): string {
    if (this.sendReturned) return "The transfer was sent, but the network has not confirmed it yet. The older reserve's balance shows whether it landed.";
    if (this.retryRefusal !== null) return `The transfer may have been sent: an earlier attempt may have reached the network before a retry was refused (${this.retryRefusal}), and it was not confirmed. The older reserve's balance shows whether it landed.`;
    return HANDED_NOT_CONFIRMED;
  }

  private abortStopText(): string {
    const why = this.abortedBy ?? this.stoppedBy ?? 'forget';
    if (this.sendReturned) return `${STOPPED[why]} The transfer had already been sent; the older reserve's balance shows whether it landed.`;
    return `${STOPPED[why]} ${this.unconfirmedText()}`;
  }

  /**
   * The Confirm opening: the key is assigned to the operation only after the lifetime check that follows the import, so
   * a late import never installs a usable signer. Each helper below keeps the signer in its own frame only, which ends
   * when it returns, so no suspended step of the flow keeps a reference the cancel cannot drop.
   */
  private async installSigner(passphrase: Secret): Promise<void> {
    const signer = await this.openKey(passphrase, 'passphrase', 'importing');
    // Checked here too, since this resumes a microtask after `openKey`'s own check.
    this.check();
    this.#signer = signer;
    // Counted for the specs only (the read-out), so a second install in one operation shows.
    if (import.meta.env.DEV) this.signerInstalls++;
  }

  /** The pre-R48 address opening: only the public key is returned; the signer is dropped when this returns. */
  private async publicKeyOf(passphrase: Secret): Promise<Uint8Array> {
    const signer = await this.openKey(passphrase, 'opening', 'opening');
    this.check();
    return Uint8Array.from(signer.publicKey);
  }

  /** The one signature (step 5): the signer is dropped as soon as it exists, on every path, and never signs again. */
  private async signOnce(message: V1Message): Promise<Uint8Array> {
    const signer = this.#signer;
    if (!signer) throw this.stopped();
    try {
      return await signV1(message, signer);
    } finally {
      this.#signer = null;
    }
  }

  /**
   * The envelope opening and the import (step 4's rows), each followed by the lifetime check. The seed `openRelayerSeed`
   * returns is a view of the decrypted buffer and is zeroed in `finally` on every path; a key imported after a cancel
   * is never assigned to the operation. The key must be the older reserve's: `vaultInfo.relayer`, or the first
   * opening's key on a pre-R48 device.
   */
  private async openKey(passphrase: Secret, opening: LegacyPhase, importing: LegacyPhase): Promise<RelayerSigner> {
    const identity = this.#identity;
    const text = passphrase.value;
    passphrase.value = null;
    if (!identity || text === null) throw this.stopped();
    this.patch({ phase: opening });
    let seed: Uint8Array | null = null;
    try {
      try {
        seed = await openRelayerSeed(identity.sealedRelayer, text);
      } catch (e) {
        this.check();
        if (e instanceof MasterStorageError && e.code === 'BadPassphraseOrTampered') throw new LegacyRefused('That passphrase does not open the older fee reserve on this device. Nothing was sent.');
        throw e;
      }
      this.#seed = seed;
      this.check();
      this.patch({ phase: importing });
      let signer: RelayerSigner;
      try {
        signer = await relayerFromSeed(seed);
      } catch (e) {
        this.check();
        // 2.4: the screen says so; nothing is stranded that today's build could reach, whose unlock needs the same import.
        throw new LegacyRefused(`This browser could not import the older key either (${e instanceof Error ? e.message : String(e)}). Nothing was sent.`);
      }
      this.check();
      if (this.sourceKey && !equalBytes(signer.publicKey, this.sourceKey)) throw new LegacyRefused('The key this passphrase opened is not the older fee reserve this device recorded, so nothing was signed or sent.');
      return signer;
    } finally {
      if (seed) seed.fill(0);
      if (this.#seed === seed) this.#seed = null;
    }
  }

  // --------------------------------------------------------------- lifetime

  /** One user-started step: counted while its awaits are pending, so the slot is released only after the last one settles (step 6). */
  private async flow<T>(body: () => Promise<T>): Promise<T> {
    this.busy++;
    try {
      return await body();
    } catch (e) {
      this.fail(e);
      throw e;
    } finally {
      this.busy--;
      this.settle();
    }
  }

  private fail(e: unknown): void {
    if (e instanceof LegacyInputRefused) {
      this.patch({ phase: 'review', error: e.message });
      return;
    }
    this.#signer = null;
    this.#wire = null;
    if (ENDED.has(this.snap.phase)) return;
    const reason = failureText(e);
    this.patch({ phase: 'failed', error: reason, outcome: { kind: 'not-sent', reason } });
  }

  /** Step 6: once ended and with no await pending, drop everything, abort the controller and free the session's slot. */
  private settle(): void {
    if (this.busy > 0 || this.releasedFlag || !ENDED.has(this.snap.phase)) return;
    this.dropSecrets();
    if (!this.controller.signal.aborted) this.controller.abort();
    this.releasedFlag = true;
    // The handle lets go of the operation, its clients and its controller; the form reads the final figures.
    this.ref.last = this.snap;
    this.ref.op = null;
    this.session.released(this);
    this.emit();
  }

  private dropSecrets(): void {
    if (this.#seed) this.#seed.fill(0);
    this.#seed = null;
    this.#signer = null;
    this.#wire = null;
    this.#identity = null;
  }

  private check(): void {
    if (this.live()) return;
    // A token change or a pending forget always arrives with the session's cancel (`invalidate`); this covers one that did not.
    if (this.stoppedBy === null) this.cancel(this.session.forgetting() ? 'forget' : 'lock');
    throw this.stopped();
  }

  private stopped(): LegacyCancelled {
    return new LegacyCancelled(this.stoppedBy ?? (this.session.forgetting() ? 'forget' : 'lock'));
  }

  /** A backoff sleep that rejects as soon as `stop()` holds: at its end, or when `cancel` wakes it. */
  private sleep(ms: number, stop: () => boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (stop()) {
        reject(this.stopped());
        return;
      }
      const wake = (): void => {
        if (!stop()) return;
        clearTimeout(timer);
        this.wakers.delete(wake);
        reject(this.stopped());
      };
      const timer = setTimeout(() => {
        this.wakers.delete(wake);
        if (stop()) reject(this.stopped());
        else resolve();
      }, ms);
      this.wakers.add(wake);
    });
  }

  private patch(p: Partial<LegacyFigures>): void {
    // An ended operation never returns to a running phase: a step that resumes after a cancel cannot reopen it.
    if (p.phase !== undefined && ENDED.has(this.snap.phase) && !ENDED.has(p.phase)) {
      const { phase: _ended, ...rest } = p;
      p = rest;
    }
    this.snap = { ...this.snap, ...p };
    // After the release the handle reads `last`, so a late patch still reaches the form.
    if (this.ref.op === null) this.ref.last = this.snap;
    this.emit();
  }

  private emit(): void {
    for (const l of [...this.listeners]) l();
  }
}
